import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtom } from "jotai";

import { graphql } from "@repo/graphql-types/gql";
import { CpsBadge, CpsTextArea } from "corpus";

import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
} from "@/components/ui/select-input/card-select";
import { useGraphQL } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import {
  isDocumentStepComplete,
  isReasonStepComplete,
  REGULATORY_JUSTIFICATION_MAX_LENGTH,
} from "@/lib/form-schemas/regulatory-document-schema";
import {
  isBindingDeclaration,
  podeEmitirReceituarioEspecial,
  visibleReasons,
} from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDocumentReasonsQuery = graphql(/* GraphQL */ `
  query RegulatoryDocumentReasonsQuery($codTipoDocumentoRegulatorio: Int!) {
    tiposDocumentosRegulatorios(
      where: { codTipoDocumentoRegulatorio: { eq: $codTipoDocumentoRegulatorio } }
    ) {
      motivos {
        codMotivoDocumento
        descricao
        exigeJustificativa
        possuiModelo
        exigeUnidadeEm
      }
    }
  }
`);

const RegulatoryDocumentReasonPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [draft, setDraft] = useAtom(regulatoryDocumentAtom);
  const { user } = Route.useRouteContext();

  const { data, isPending } = useGraphQL(RegulatoryDocumentReasonsQuery, {
    codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
  });
  const reasons = visibleReasons(
    data?.tiposDocumentosRegulatorios?.[0]?.motivos ?? [],
    isBindingDeclaration(draft.codTipoDocumentoRegulatorio),
    podeEmitirReceituarioEspecial(user.codConselhoClasse),
  );

  const canContinue = isReasonStepComplete(draft);

  const handleSelectReason = (value: string): void => {
    const reason = reasons.find((option) => option.codMotivoDocumento === Number(value));
    if (!reason) return;

    setDraft((prev) => ({
      ...prev,
      codMotivoDocumento: reason.codMotivoDocumento,
      reasonLabel: reason.descricao,
      requiresJustification: reason.exigeJustificativa,
      justification: reason.exigeJustificativa ? prev.justification : "",
      requiresLocationIn: reason.exigeUnidadeEm,
      selectedLocations: [],
    }));
  };

  const handleJustificationChange = (value: string): void => {
    setDraft((prev) => ({
      ...prev,
      justification: value.slice(0, REGULATORY_JUSTIFICATION_MAX_LENGTH),
    }));
  };

  const handleContinue = (): void => {
    if (!canContinue) return;

    trackEvent("Motivo de Documento Regulatório Selecionado", {
      codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
      codMotivoDocumento: draft.codMotivoDocumento,
      motivo: draft.reasonLabel,
      possuiJustificativa: draft.requiresJustification,
    });

    void navigate({
      to: isBindingDeclaration(draft.codTipoDocumentoRegulatorio)
        ? "/settings/regulatory-documents/state"
        : "/settings/regulatory-documents/locations",
    });
  };

  return (
    <ProgressRoundedLayout progress={1 / 3} className="pb-0">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-4">
          <div>
            <CpsBadge color="neutral">{draft.documentName}</CpsBadge>
          </div>
          <h1 className="text-xl font-medium leading-tight text-neutral-700">
            Por que você precisa deste documento?
          </h1>
          <p className="-mt-2 text-sm text-neutral-500">
            Selecione o motivo na lista abaixo
          </p>

          {isPending ? (
            <FullPageSpinner />
          ) : (
            <CardSelectRoot
              name="regulatory-document-reason"
              mode="single"
              defaultValue={
                draft.codMotivoDocumento !== 0
                  ? String(draft.codMotivoDocumento)
                  : undefined
              }
              onChange={handleSelectReason}
            >
              <CardSelectItemContainer className="gap-3">
                {reasons.map((reason) => (
                  <CardSelectItem
                    key={reason.codMotivoDocumento}
                    value={String(reason.codMotivoDocumento)}
                  >
                    {reason.descricao}
                  </CardSelectItem>
                ))}
              </CardSelectItemContainer>
            </CardSelectRoot>
          )}

          {draft.requiresJustification ? (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="regulatory-document-justification"
                className="text-sm font-medium text-neutral-600"
              >
                Conte um pouco mais*
              </label>
              <CpsTextArea
                id="regulatory-document-justification"
                value={draft.justification}
                onChange={(event) => {
                  handleJustificationChange(event.target.value);
                }}
                placeholder="Explique brevemente por que você precisa deste documento"
                maxLength={REGULATORY_JUSTIFICATION_MAX_LENGTH}
                required
                rows={3}
              />
              <span className="self-end text-xs text-neutral-400">
                {draft.justification.length}/{REGULATORY_JUSTIFICATION_MAX_LENGTH}
              </span>
            </div>
          ) : null}
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button fullWidth onClick={handleContinue} disabled={!canContinue}>
            Continuar
          </Button>
        </div>
      </div>
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/reason")({
  component: RegulatoryDocumentReasonPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);
    if (!isDocumentStepComplete(draft)) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }
  },
});
